import { PEPTIDE_CATEGORIES, CATEGORY_COLORS } from "../../data/peptideCatalog";
import { getOrderOrigin } from "../../utils/peptideOrderOrigin";
import { PEPTIDE_STATUSES, canUseManualPeptidePaymentActions } from "../../utils/peptideOrderPermissions";

// Shared breakpoint for peptide surfaces that swap desktop controls for a
// purpose-built phone layout. Keep JS matchMedia checks aligned with the
// mobile SCSS boundary.
export const PEPTIDE_MOBILE_QUERY = "(max-width: 768px)";

// Shared price formatter for the whole peptides module (catalog, tier
// pricing, order cards, KPIs, invoice). Renders cents with thousands
// separators per the design contract ($1,234.56) — prices flow through the
// system as integer cents, this is presentation only. Null → "--".
const PRICE_FMT = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export const formatPrice = (cents) => {
  if (cents == null) return "--";
  return PRICE_FMT.format(cents / 100);
};

export const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "--");

// Date + wall-clock time ("7/11/2026, 2:05 PM") for queue surfaces where the
// received TIME matters — the Pending Shipment lane processes FIFO, and two
// same-day cards need the time to make the ordering legible. Accepts ISO
// strings or Unix millis (createdOn arrives as either).
export const formatDateTime = (value) => {
  if (value == null) return "--";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "--";
  return `${d.toLocaleDateString()}, ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
};

// Amazon-style header-strip date — "Jul 2, 2026". formatDate stays the
// terse locale form for event/tracking timestamps.
export const formatShortDate = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "--" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// Statuses where the fulfillment method (Ship ↔ Pickup) can still be changed.
// MIRRORS the backend gate in "Peptide Order - Update" — once fulfillment has
// started (shipped / readyForPickup) or the order is terminal, the method is
// history, not intent. Keep the two lists in sync.
const FULFILLMENT_EDITABLE_STATUSES = new Set([
  "draft",
  "payment_pending",
  "payment_authorized",
  "payment_capturing",
  "confirmed",
  "awaiting_restock",
]);

export const canEditPeptideFulfillmentMethod = (order) => FULFILLMENT_EDITABLE_STATUSES.has(order?.status);

// Refetch-resilience for the packed "Ready" marks. The board's list refetch
// replaces `orders` wholesale; until the backend's List projection ships the
// `readyMarked` field, fresh rows OMIT the key entirely and every refetch
// (which runs after every drag) would silently wipe the team's marks. Carry
// the previous mark forward ONLY when the key is absent from the fresh row.
// When the server DOES send the key (new BE always projects it — explicit
// null when unmarked), the server wins, so another user's un-mark is never
// resurrected from local memory.
export const carryReadyMarks = (prevOrders, nextOrders) => {
  if (!Array.isArray(nextOrders) || !Array.isArray(prevOrders) || prevOrders.length === 0) return nextOrders;
  const prevMarks = new Map(prevOrders.filter((o) => o?.readyMarked && o.id != null).map((o) => [o.id, o.readyMarked]));
  if (prevMarks.size === 0) return nextOrders;
  return nextOrders.map((o) =>
    o && o.id != null && !("readyMarked" in o) && prevMarks.has(o.id) ? { ...o, readyMarked: prevMarks.get(o.id) } : o
  );
};

// Relative-day classifier extracted from the My Orders list's local
// formatRelativeDate so the order-card files can share it. Returns a key,
// not display text — callers pick their own i18n string per key.
export const relativeDayKey = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "today";
  const yest = new Date(now.getTime() - 86400000);
  if (d.toDateString() === yest.toDateString()) return "yesterday";
  return null;
};

// "A, B, +2" one-line product summary — moved from PatientPeptideOrders so
// the card components can share it. Non-array items must not crash (list
// robustness test guards this).
export const formatProductSummary = (items) => {
  if (!Array.isArray(items) || items.length === 0) return "";
  const names = items.map((it) => it?.productName).filter(Boolean);
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")}, +${names.length - 2}`;
};

// AOD 9604 detection — used to flip a product's cart line onto the branded
// full-bleed background (vial + hex pattern baked into one image). Matches the
// same id/slug/name signals PeptideVial.js uses so the two never disagree;
// kept here (not imported from PeptideVial) so non-vial surfaces can reuse it
// without pulling in the image imports. Currently a single-product visual test.
const normalizeKey = (v) =>
  String(v ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const productKeys = (product) => [product?.id, product?.slug, product?.name, product?.subtitle].map(normalizeKey);

export const isAodProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && (v.includes("aod") || v.includes("9604")));
};

// CJC 1295 / Ipamorelin combo — both tokens must appear so the standalone
// "CJC 1295" doesn't also match.
export const isCjcIpamorelinProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("cjc") && v.includes("ipamorelin"));
};

// Branded cart-line treatment (full-bleed vial + hex art). Returns the asset
// key driving the `--branded-vial` CSS variable, or null for normal products.
// Add a product here + a matching `--<key>` SCSS override to brand it.
export const isBpc157Product = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && (v.includes("bpc") || v.includes("157")));
};

export const isGonadorelinProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("gonadorelin"));
};

// Standalone Ipamorelin — the CJC/Ipamorelin combo is matched first in
// getBrandedVial, so this only catches the standalone product.
export const isIpamorelinProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("ipamorelin"));
};

export const isGlowProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("glow"));
};

export const isKlowProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("klow"));
};

// GHK-CU — checked after GLOW/KLOW, whose subtitles also contain "GHK-CU".
export const isGhkCuProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("ghkcu"));
};

// Follistatin — match the catalog's one-l "Folistatin" spelling too.
export const isFollistatinProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && (v.includes("follistatin") || v.includes("folistatin")));
};

export const isDsipProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("dsip"));
};

// Sermorelin — also match the "Semorelin" spelling printed on the vial art.
export const isSermorelinProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && (v.includes("sermorelin") || v.includes("semorelin")));
};

export const isEpithalonProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("epithalon"));
};

// NAD+ — startsWith avoids matching "goNADorelin".
export const isNadProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.startsWith("nad"));
};

export const isHghProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("hgh"));
};

export const isHcgProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("hcg"));
};

// IGF-1 (LR3) — catalog name "IGF-1 LR3" → "igf1lr3"; "igf" is unambiguous.
export const isIgfProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("igf"));
};

export const isKisspeptinProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("kisspeptin"));
};

// MOTS-C → normalizes to "motsc".
export const isMotsCProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("motsc"));
};

// PT-141 → normalizes to "pt141".
export const isPt141Product = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("pt141"));
};

export const isOxytocinProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("oxytocin"));
};

// GLP-3 (retatrutide) — catalog name "Reta GLP-3". Match the "reta" nickname or
// the "glp3" token; neither overlaps GLP-1/GLP-2.
export const isRetaGlp3Product = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && (v.includes("glp3") || v.includes("reta")));
};

// GLP-1 (semaglutide) — catalog names "GLP-1" and "Sema GLP-1" both normalize
// to contain "glp1". Match on "glp1" (NOT bare "sema") so this never steals
// Semax ("semax") or Sermorelin.
export const isSemaGlp1Product = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("glp1"));
};

export const isSemaxProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("semax"));
};

export const isSelankProduct = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("selank"));
};

// TB 500 → normalizes to "tb500".
export const isTb500Product = (product) => {
  if (!product) return false;
  return productKeys(product).some((v) => v && v.includes("tb500"));
};

export const getBrandedVial = (product) => {
  if (isAodProduct(product)) return "aod";
  if (isCjcIpamorelinProduct(product)) return "cjc";
  // GLOW / KLOW before GHK-CU and BPC — their subtitles list those ingredients.
  if (isGlowProduct(product)) return "glow";
  if (isKlowProduct(product)) return "klow";
  if (isGhkCuProduct(product)) return "ghkcu";
  if (isBpc157Product(product)) return "bpc";
  if (isGonadorelinProduct(product)) return "gonadorelin";
  if (isIpamorelinProduct(product)) return "ipamorelin";
  if (isSermorelinProduct(product)) return "sermorelin";
  if (isEpithalonProduct(product)) return "epithalon";
  if (isNadProduct(product)) return "nad";
  if (isFollistatinProduct(product)) return "follistatin";
  if (isDsipProduct(product)) return "dsip";
  if (isHghProduct(product)) return "hgh";
  if (isHcgProduct(product)) return "hcg";
  if (isIgfProduct(product)) return "igf";
  if (isKisspeptinProduct(product)) return "kisspeptin";
  if (isMotsCProduct(product)) return "motsc";
  if (isPt141Product(product)) return "pt141";
  if (isOxytocinProduct(product)) return "oxytocin";
  // GLP-3 before GLP-1: both contain "glp", but the tokens ("glp3" vs "glp1")
  // are distinct, so order is belt-and-suspenders.
  if (isRetaGlp3Product(product)) return "glp3";
  if (isSemaGlp1Product(product)) return "glp1";
  if (isSemaxProduct(product)) return "semax";
  if (isSelankProduct(product)) return "selank";
  if (isTb500Product(product)) return "tb500";
  if (isBacteriostaticWaterProduct(product)) return "bacwater";
  return null;
};

// ── OCG brand-color variant per peptide ────────────────────────────────
// Maps each product name to one of the 7 OCG brand colors. Used by the
// mobile label tile to flip the surface + hex pattern + foreground tokens.
// Match is name-based (case + space tolerant) so renames in the catalog
// don't require keeping IDs in sync. Unknown products fall back to "sand".
//
// Color buckets (from the brand sheet):
// - sand  → default / GHRH analogs / supportive (AOD, Tesa, BPC, CJC, etc.)
// - gold  → metabolic / GLP-2 / NAD / Oxytocin / PT-141 / Reta GLP-3
// - blue  → GLP-1 / repair / TB-500 / Wolverine / Kisspeptin / GLOW/KLOW 70
// - green → growth & hormones (Gonadorelin, HGH, IGF-1 LR3, Semax, Sema GLP)
// - black → research / sleep / longevity (DSIP, Epithalon, Folistatin,
//           Selank, Sermorelin, Tesa/Ipa combo)
// - bone  → supplies & solvents (Bacteriostatic Water, Dynacord, Glutathione,
//           L-Carnitine)
// - slate → fallback for unmapped products
const OCG_VARIANT_BY_NAME = {
  // Sand
  "aod 9604": "sand",
  tesamorelin: "sand",
  "bpc 157": "sand",
  "cjc 1295": "sand",
  "cjc 1295 / ipamorelin": "sand",
  ipamorelin: "sand",
  hcg: "sand",
  "ghk-cu": "sand",
  // Gold
  "glp-2": "gold",
  "klow 80": "gold",
  "mots-c": "gold",
  "nad+": "gold",
  oxytocin: "gold",
  "pt-141": "gold",
  "reta glp-3": "gold",
  // Blue
  "glp-1": "blue",
  "tb 500": "blue",
  wolverine: "blue",
  kisspeptin: "blue",
  "glow 70": "blue",
  "klow 70": "blue",
  // Green
  gonadorelin: "green",
  hgh: "green",
  "igf-1 lr3": "green",
  semax: "green",
  "sema glp-1": "green",
  // Black
  dsip: "black",
  epithalon: "black",
  "folistatin 344 (95%)": "black",
  folistatin: "black",
  selank: "black",
  sermorelin: "black",
  "tesamorelin / ipamorelin": "black",
  // Bone
  "bacteriostatic water": "bone",
  dynacord: "bone",
  glutathione: "bone",
  "l-carnitine": "bone",
};

// Returns the OCG variant key for a product. Normalizes spacing/case so
// "AOD-9604", "aod9604", "AOD 9604" all hit the same bucket.
export const ocgVariantForProduct = (product) => {
  if (!product) return "sand";
  const explicit = (product.ocgVariant || "").toString().toLowerCase();
  if (explicit && ["sand", "gold", "blue", "green", "black", "bone", "slate"].includes(explicit)) {
    return explicit;
  }
  const key = (product.name || "").toString().toLowerCase().replace(/[_-]/g, "-").replace(/\s+/g, " ").trim();
  return OCG_VARIANT_BY_NAME[key] || "sand";
};

// Normalize state-code inputs to uppercase, trimmed 2-letter codes for the
// patient state-availability gate. Returns null for non-strings, empties,
// or anything that isn't exactly 2 letters — fail-closed so longer values
// like "OHIO" route through the missing-state UI instead of a falsely
// "available" tile we can't compare against the restricted list.
export const normalizeStateCode = (s) => {
  if (typeof s !== "string") return null;
  const trimmed = s.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(trimmed) ? trimmed : null;
};

// Read a patient-decorated field with es-locale fallback. For es: prefer
// `${base}Es`, fall back to the base. For en (or any non-es locale): just
// the base. Used by every place that renders patient catalog text.
export const pickPatientFieldEs = (product, base, isEs) => {
  if (!product) return undefined;
  return isEs ? product[`${base}Es`] || product[base] : product[base];
};

// Legacy patient orders (placed before the pay-at-submit redesign) often have
// totalCents=0 stored on the order itself — even though the items array carries
// per-item priceCents. Same for placedAt: pre-redesign orders only have
// createdAt or lastStateChangeAt populated. The helpers below derive a usable
// total / date from whichever fields are populated, in order of preference.

export const derivePatientOrderTotalCents = (order) => {
  if (!order) return null;
  // totalCents is product-only (spec money contract) — the displayed total is
  // always product + shipping fee. Legacy orders carry no shippingCents (+0).
  if (typeof order.totalCents === "number" && order.totalCents > 0) return order.totalCents + (order.shippingCents || 0);
  const items = Array.isArray(order.items) ? order.items : [];
  const itemsSum = items.reduce((s, i) => s + (i?.priceCents || 0) * (i?.quantity || 0), 0);
  if (itemsSum > 0) return itemsSum + (order.shippingCents || 0);
  return null;
};

export const derivePatientOrderPlacedAt = (order) => {
  const raw = order?.placedAt || order?.createdAt || order?.createdOn || order?.lastStateChangeAt || null;
  // createdOn arrives as epoch-ms from the backend; normalize to ISO so downstream
  // callers (commission math) can safely pass to new Date() without ambiguity.
  if (typeof raw === "number") return new Date(raw).toISOString();
  return raw;
};

// The dollar total for a single peptide order shard — its own line items for a
// split parent/child, the authoritative totalCents for a non-split order.
//
// Safe for AGGREGATE revenue that sums EVERY shard (parent + child reconcile to
// the original charge — see commissions/peptideMetrics.js). NOT safe for a
// single order's revenue in isolation: a split PARENT alone returns only its
// remaining items (~50% of the original charge) since the rest moved to the
// child. Single-order / per-rep math that does NOT also iterate the children
// (e.g. utils/peptideAnalytics/feeEstimation.js, hooks/usePeptideRepStats.js)
// must still read order.totalCents.
//
// For a split shard the order-level totalCents no longer reflects that card's
// contents: the child is born with totalCents = 0 (payment lives on the parent
// — "Paid via #parent"), and the split PARENT keeps the whole original charge
// even after its back-ordered line moves to the child. In both cases show the
// sum of THIS card's line items so each card's total matches its pick list and
// the parent + child sum back to the original charge. A child carries
// `parentOrderId`; a split parent carries `childBackOrderId`.
//
// Money contract (spec 2026-07-03-peptide-shipping-method): order.totalCents
// is PRODUCT-ONLY; the server-resolved shipping fee lives separately in
// order.shippingCents and the charged amount is their sum. So every branch
// here adds shippingCents on top — legacy orders (shippingCents null) are
// unchanged. For split shards the fee rides the PARENT (children never carry
// shippingCents), so parent + child still reconcile to the original charge.
export const getPeptideCardTotalCents = (order) => {
  if (!order) return 0;
  const items = Array.isArray(order.items) ? order.items : [];
  const itemsSum = items.reduce((s, i) => s + (i?.priceCents || 0) * (i?.quantity || 0), 0);
  const shipping = order.shippingCents || 0;
  const isSplitShard = Boolean(order.parentOrderId) || Boolean(order.childBackOrderId);
  if (!isSplitShard && typeof order.totalCents === "number" && order.totalCents > 0) {
    // If the (product-only) totalCents exceeds the item sum, the item list was
    // trimmed for a back-order split but totalCents was never updated by the
    // backend. Use itemsSum so only delivered items count toward revenue.
    if (itemsSum > 0 && itemsSum < order.totalCents) return itemsSum + shipping;
    return order.totalCents + shipping;
  }
  return itemsSum + shipping;
};

// PEPTIDE_STATUSES is the canonical registry (peptideOrderPermissions.js) —
// every entry's `sortOrdinal` is read directly, so a status added there per
// its own documented "MUST fill" contract can never silently fall through a
// second, disconnected table here. Unrecognized status strings (defensive
// only) sort last.
const statusSortOrdinal = (status) => PEPTIDE_STATUSES[status]?.sortOrdinal ?? 10;

const placedAtMs = (order) => new Date(derivePatientOrderPlacedAt(order) || 0).getTime();

const compareBySortField = (a, b, field) => {
  if (field === "total") return getPeptideCardTotalCents(a) - getPeptideCardTotalCents(b);
  if (field === "status") return statusSortOrdinal(a.status) - statusSortOrdinal(b.status);
  return placedAtMs(a) - placedAtMs(b); // "placedAt" (default)
};

// Shared sort for the patient "My Orders" and provider "Order History" list
// pages (spec 2026-07-08-peptide-order-list-sorting-design.md). Returns a
// NEW array — never mutates `orders`. Ties on the chosen field fall back to
// placedAt descending (newest first) regardless of the chosen field/direction.
export const sortPeptideOrders = (orders, { field = "placedAt", direction = "desc" } = {}) => {
  const list = Array.isArray(orders) ? [...orders] : [];
  const sign = direction === "asc" ? 1 : -1;
  return list.sort((a, b) => {
    const primary = compareBySortField(a, b, field);
    if (primary !== 0) return sign * primary;
    return placedAtMs(b) - placedAtMs(a);
  });
};

// Split-aware accounting context over a SET of peptide orders (a parent and its
// back-order child can each appear in the list). Build it once, then ask it about
// any order. It handles two split shapes:
//   1. Normal split — getPeptideCardTotalCents already returns each shard's own
//      items, so parent + child reconcile to the original charge.
//   2. Legacy split where the backend never trimmed the parent's items array, so
//      the parent still carries the child's backordered line and any sum over all
//      shards double-counts it.
// Returns:
//   revenueCentsFor(order) — that order's de-duplicated revenue in cents.
//   isUntrimmedParent(order) — true for a case-2 parent.
//   dedupedItemsFor(order) — order.items with an untrimmed parent's overlapping
//      QUANTITIES reduced by the child's (so a per-item sum over every shard counts
//      the shared backordered line once). Other orders return items unchanged.
// Pass the full order list so the parent<->child maps are complete; archived and
// cancelled children are excluded.
export const makePeptideSplitContext = (orders) => {
  const list = Array.isArray(orders) ? orders : [];
  const childItemsSumByParentId = {};
  const childVariantsByParentId = {};
  const childQtyByParentVariant = {};
  const byId = {};
  for (const o of list) {
    if (o && o.id != null && byId[o.id] === undefined) byId[o.id] = o;
    if (!o || o.isArchived || !o.parentOrderId || o.status === "cancelled") continue;
    const items = Array.isArray(o.items) ? o.items : [];
    const sum = items.reduce((s, i) => s + (i?.priceCents || 0) * (i?.quantity || 0), 0);
    childItemsSumByParentId[o.parentOrderId] = (childItemsSumByParentId[o.parentOrderId] || 0) + sum;
    if (!childVariantsByParentId[o.parentOrderId]) childVariantsByParentId[o.parentOrderId] = new Set();
    if (!childQtyByParentVariant[o.parentOrderId]) childQtyByParentVariant[o.parentOrderId] = {};
    for (const i of items) {
      const key = i?.variantId || i?.productId;
      if (!key) continue;
      childVariantsByParentId[o.parentOrderId].add(key);
      childQtyByParentVariant[o.parentOrderId][key] = (childQtyByParentVariant[o.parentOrderId][key] || 0) + (i?.quantity || 0);
    }
  }

  // Detect parent via childBackOrderId (server-set) OR via childItemsSumByParentId
  // (a child listed this order as its parentOrderId). The latter handles the case
  // where the backend creates the child with parentOrderId but never sets
  // childBackOrderId on the parent — without this, the parent would count its full
  // totalCents while the child also counts its items.
  // Use `in` for the map check, not Boolean() — a $0-priced child accumulates
  // 0 in childItemsSumByParentId, and Boolean(0) would wrongly return false.
  const isKnownParent = (order) => Boolean(order?.childBackOrderId) || (order?.id != null && order.id in childItemsSumByParentId);

  // Value-proven untrimmed: a known parent whose current item value still covers
  // its original charge (totalCents). Require a POSITIVE totalCents to compare
  // against: `order.totalCents || 0` would collapse a 0/missing total to 0, making
  // `total >= 0` always true and wrongly flagging a correctly-trimmed partial
  // split. With no positive total we can't prove untrimmed, so we don't.
  // This is the signal REVENUE uses to subtract the child's value off the parent.
  // It deliberately does NOT require the child's variant id to still appear on the
  // parent: a restocked back-order child can come back with a re-keyed variant/
  // product id, and the authoritative total already proves the parent never had
  // its line removed. Variant matching is too fragile to gate a money figure on.
  const looksUntrimmedByTotal = (order) => {
    if (!isKnownParent(order)) return false;
    // Compare PRODUCT value to the product-only totalCents — the card total
    // includes the parent's shippingCents, which would otherwise make every
    // fee-bearing trimmed parent look untrimmed (total + fee >= totalCents is
    // trivially true) and wrongly subtract the child's value from revenue.
    const productCents = getPeptideCardTotalCents(order) - (order?.shippingCents || 0);
    return Number.isFinite(order.totalCents) && order.totalCents > 0 && productCents >= order.totalCents;
  };

  // Untrimmed AND the parent's items still literally share a variant with a live
  // child. This stricter, variant-aware form is what ITEM-LEVEL de-dup
  // (dedupedItemsFor → commission spread) needs, because it has to know WHICH
  // parent line to reduce. Revenue uses the looser looksUntrimmedByTotal above.
  const isUntrimmedParent = (order) => {
    if (!looksUntrimmedByTotal(order)) return false;
    const childVariants = childVariantsByParentId[order.id];
    const parentItems = Array.isArray(order.items) ? order.items : [];
    return Boolean(childVariants && parentItems.some((i) => childVariants.has(i?.variantId || i?.productId)));
  };

  const revenueCentsFor = (order) => {
    const total = getPeptideCardTotalCents(order);
    // Subtract the child's value off any total-proven untrimmed parent, even when
    // the child's variant id no longer matches a parent line — otherwise the split
    // shard's value is paid twice (the user-reported back-order double-count).
    if (looksUntrimmedByTotal(order)) return Math.max(0, total - (childItemsSumByParentId[order.id] || 0));
    return total;
  };

  // Per-item de-dup for callers that sum spread / units over items (commission).
  // Revenue subtracts the child's dollars off the parent and KEEPS the child; this
  // mirrors that direction at the item level — reduce the untrimmed parent's shared
  // quantities by the child's, and always keep the child counted as itself. That
  // avoids the failure modes of "just skip the child": the line is never dropped
  // when the parent isn't in the sum (cancelled/draft) or when the child carries a
  // variant the parent doesn't list. A whole-line move reduces to qty 0 and drops;
  // a partial split keeps the parent's remaining quantity.
  const dedupedItemsFor = (order) => {
    const items = Array.isArray(order?.items) ? order.items : [];
    if (!isUntrimmedParent(order)) return items;
    const childQty = childQtyByParentVariant[order.id] || {};
    const out = [];
    for (const i of items) {
      const key = i?.variantId || i?.productId;
      const qty = (i?.quantity || 0) - (key ? childQty[key] || 0 : 0);
      if (qty > 0) out.push({ ...i, quantity: qty });
    }
    return out;
  };

  return { revenueCentsFor, isUntrimmedParent, dedupedItemsFor, isKnownParent };
};

// Back-compat: revenue callers only need the per-order resolver.
export const makePeptideRevenueResolver = (orders) => makePeptideSplitContext(orders).revenueCentsFor;

// The org a peptide order is ATTRIBUTED to for rep commission/stats — mirrors the
// backend lib/peptideOrders.js `effectiveCommissionOrgId` so the client-side
// Sales Dashboard estimate buckets orders the SAME way the authoritative
// rep-earnings rollup (BE `repEarningsCents`) does. Precedence:
//   assignedProviderId  (a manufacturer placed the order FOR a provider) >
//   referringProviderId (a patient ordered UNDER a provider) >
//   organizationId      (the provider placed it themselves).
// Walk-in / in-store manufacturer orders carry neither of the first two, so they
// collapse to organizationId (the manufacturer's own org → resolves to no rep,
// which is correct — a walk-in has no onboarding rep). Empty-string ids fall
// through; returns null when nothing resolves.
export const effectiveCommissionOrgId = (order) =>
  order?.assignedProviderId || order?.referringProviderId || order?.organizationId || null;

// True when a peptide order's rep-commission basis must be repriced from the
// retail price the patient paid to the provider's tier price: a patient order
// carries referringProviderId and NO assignedProviderId. Mirrors the backend
// lib/peptideOrders.js `isPatientCommissionBasis`. Manufacturer master orders
// (assignedProviderId set) and provider-placed orders (neither set) already
// transact at the provider tier, so their stored item.priceCents is already the
// correct buyer basis and must NOT be repriced.
export const isPatientCommissionBasis = (order) => Boolean(order?.referringProviderId) && !order?.assignedProviderId;

export const getCategoryTags = (tags) =>
  (tags || [])
    .map((tag) => {
      const cat = PEPTIDE_CATEGORIES.find((c) => c.key === tag);
      return cat ? { key: tag, label: cat.label, labelKey: cat.labelKey, colorClass: CATEGORY_COLORS[tag] } : null;
    })
    .filter(Boolean);

export const calcMargin = (costCents, sellCents) => {
  if (costCents == null || !sellCents) return null;
  return Math.round(((sellCents - costCents) / sellCents) * 100);
};

export const calcSellFromMargin = (costCents, marginPct) => {
  if (costCents == null || marginPct >= 100) return null;
  return Math.round(costCents / (1 - marginPct / 100));
};

// Margin can be negative (sell below cost) for promo / loss-leader tiers.
// We clamp to [-95, 95] because the slider uses these as visual bounds.
export const clampMargin = (pct) => {
  if (pct == null || Number.isNaN(pct)) return null;
  if (pct < -95) return -95;
  if (pct > 95) return 95;
  return pct;
};

export const formatMargin = (pct) => {
  if (pct == null) return "--";
  return pct + "%";
};

export const getMarginColor = (pct) => {
  if (pct == null) return "muted";
  if (pct <= 0) return "loss";
  if (pct <= 25) return "warn";
  return "good";
};

// Splits a compound name on " / " into parts for two-line rendering.
// Returns a single-element array for non-compound names. Moved from
// PricingTierManager.js so multiple custom-pricing editors can share.
export const splitCompound = (text) =>
  String(text ?? "")
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean);

// ── Order-type classifiers ──
// One source of truth for "what kind of peptide order is this" so the kanban,
// draft review, fulfillment view, and order detail screens all classify the
// same order identically. `assignType` is set by both manufacturer and
// provider checkout (PeptideCheckout sets "patient" for provider patient
// orders explicitly), which means these classifiers can rely on a typed
// field instead of brittle shipping-shape heuristics.

export const isPeptidePatientOrder = (order) => order?.assignType === "patient";

export const isPeptideMasterOrder = (order) => order?.assignType === "provider";

// Phase-A split-fulfillment helpers — child orders carry parentOrderId set by
// the backend Split Ship process when stock can't cover the whole order. They
// have no independent payment; the parent's payment intent covers them.
export const isPeptideChildOrder = (order) => Boolean(order?.parentOrderId);

// Parent + child orders share the same human-facing order number — the BO
// chip on the kanban card and the "Paid via #…" badge on the child are the
// visual differentiators. Earlier iterations appended "-BO" to the child's
// display id, but that diverged from the user's mental model of "one logical
// order, two fulfillment cards." Keep them unified.
export const getDisplayId = (order) => {
  if (!order) return "";
  if (isPeptideChildOrder(order)) {
    return order.parentOrderId;
  }
  return order.id || "";
};

// UI label for the "Paid via #PEP-…" badge that appears on child orders.
// Returns null for parent orders so callers can render-or-skip cleanly.
export const getPaidViaLabel = (order) => {
  if (!isPeptideChildOrder(order)) return null;
  return `Paid via #${order.paidViaOrderId}`;
};

// Shared status → display label, used by every order-detail-shaped surface
// (PeptideOrderDetail.js, ProviderOrderCardBody.js). `t` is the caller's
// i18n function; falls back to the registry's raw label/status string.
export const getPeptideStatusLabel = (status, t) => {
  const def = PEPTIDE_STATUSES[status];
  if (!def) return status;
  return def.labelKey ? t(def.labelKey, def.label) : def.label;
};

// Shared pickup-vs-shipping address line, used by every order-detail-shaped
// surface (PeptideOrderDetail.js, ProviderOrderCardBody.js). Pickup orders
// read pickupLocation; everything else reads shipping. Returns "" (not a
// falsy non-string) when neither is populated, so callers can render it
// directly without an extra guard.
export const getOrderAddressValue = (order) =>
  order.fulfillmentType === "pickup" && order.pickupLocation
    ? [
        order.pickupLocation.name,
        order.pickupLocation.address,
        [order.pickupLocation.city, order.pickupLocation.state, order.pickupLocation.zip].filter(Boolean).join(" "),
      ]
        .filter(Boolean)
        .join(" · ")
    : order.shipping?.address || order.shipping?.city
      ? [order.shipping.address, [order.shipping.city, order.shipping.state, order.shipping.zip].filter(Boolean).join(" ")]
          .filter(Boolean)
          .join(", ")
      : "";

// Payment-mutation gate used by order-detail to hide Confirm Payment, Upload
// Receipt, Charge Card, Refund, etc. on child orders. Frontend convenience;
// the backend MUST also enforce this (see lib/peptideOrders.js assertIsParentOrder
// in Phase B).
export const canModifyPaymentOnOrder = (order) => canUseManualPeptidePaymentActions(order);

export const isPeptidePickupOrder = (order) => order?.fulfillmentType === "pickup";

// A pickup order that STARTED as a ship order (staff applied a Ship → Pickup
// switch; the backend writes a change-fulfillment history row on every
// switch). Its saved shipping block is a PRESERVED ship-to — kept so
// switching back is one confirm away — and must never be displayed or
// printed as the active pickup location. Shared by the fulfillment view and
// both PDF models (invoice + packing slip) so the rule can't drift again —
// the invoice carried a stale copy of this chain for one release and printed
// the customer's home address as "Pickup Location".
export const wasPeptideOrderSwitchedToPickup = (order) =>
  isPeptidePickupOrder(order) && Array.isArray(order?.history) && order.history.some((h) => h && h.field === "fulfillmentType");

// Where is this order physically going? Pickup orders resolve
// pickupLocation → referringClinicAddress → the saved shipping block, EXCEPT
// when that block is a preserved ship-to (see above) — then no address beats
// the wrong address. Ship orders always resolve to the shipping block.
// Returns {} rather than null so callers can destructure field-by-field.
export const resolvePeptideDestinationAddress = (order) => {
  if (!isPeptidePickupOrder(order)) return order?.shipping || {};
  return (
    order?.pickupLocation ||
    order?.referringClinicAddress ||
    (wasPeptideOrderSwitchedToPickup(order) ? null : order?.shipping) ||
    {}
  );
};

export const isPeptideProviderPlaced = (order) => order?.source === "provider" || order?.source === "patient-intake";

// Ship-to-patient unifies two writers: provider checkout sets
// `assignType: "patient"` (no shippingDestination); patient referral checkout
// sets `shippingDestination: "patient"` (no assignType). Either means the
// package is going to a patient address.
export const isPeptideShipToPatient = (order) => order?.assignType === "patient" || order?.shippingDestination === "patient";

// Distinct from ship-to-patient: only true when the order originated from a
// patient referral link, not when a provider placed a patient order directly.
export const isPeptideReferralLinkOrder = (order) => order?.source === "patient-intake";

// Adaptive "who is this order for / who placed it" resolver for the kanban
// card. Returns a stable { key, labelKey, value } so the desktop meta-grid and
// the mobile header chip render identically (single source of truth), and the
// component owns translation (this helper stays pure / i18n-free / unit-testable).
//
//   - patient destination  → the PATIENT (shipping name)
//   - assignType "instore" → the STAFF who rang up the walk-in sale
//   - everything else       → the CLINIC / org name
//
// For clinic-stock orders `shipping` is the clinic (PeptideCheckout's
// getClinicAddress stamps the clinic into shipping.firstName), so we MUST NOT
// read shipping outside the patient branch — that's the ambiguity this replaces.
const cardActorPatientName = (order) => {
  const first = order?.shipping?.firstName || "";
  const last = order?.shipping?.lastName || "";
  const name = `${first} ${last}`.trim();
  return name || null;
};

// Fallback placer for orders created before the dedicated placedByName field:
// read the "created" entry from the order's history log. Scoped to
// manufacturer-placed orders (source "manufacturer"), where that entry is the
// real staff member. Provider/patient orders are created through a cross-org
// wrapper whose history editor is a service identity, not the true placer — so
// we deliberately skip them and let the clinic name show until the authoritative
// placedByName is projected for them.
const cardActorHistoryPlacer = (order) => {
  if (getOrderOrigin(order) !== "manufacturer") return null;
  const entries = Array.isArray(order?.history) ? order.history : [];
  const created = entries.find((h) => h && h.field === "created");
  const label = created && typeof created.editorLabel === "string" ? created.editorLabel.trim() : "";
  return label || null;
};

// In-store walk-in placer names arrive as "<station/role> - <person>" (e.g.
// "Front Desk - J. Kim"); the card shows just the person. Split on the LAST
// " - " so a station that itself contains a dash still resolves to the name.
// Plain names and credential/title forms ("Dr. Smith", "Jane Smith, MD") have
// no " - " separator, so they pass through untouched.
const instorePlacerPerson = (name) => {
  if (typeof name !== "string") return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  const idx = trimmed.lastIndexOf(" - ");
  const person = idx >= 0 ? trimmed.slice(idx + 3).trim() : trimmed;
  return person || trimmed;
};

// Order-TYPE identity resolver for the kanban card. Returns a stable
// { key, labelKey, value } with exactly three outcomes so the card renders one
// unambiguous identifier instead of a placer name that hides what KIND of order
// it is. Precedence:
//   1. patient destination         → the PATIENT (shipping name)
//   2. assignType === "instore"    → the STAFF who rang up the walk-in sale
//   3. everything else             → the CLINIC / org name
// `value` may be null when the name isn't resolvable (rare); the card then shows
// the label alone. Stays pure / i18n-free (returns keys) so it's unit-testable.
export const resolveCardActor = (order) => {
  const clinicNone = { key: "clinic", labelKey: "peptides.kanban.metaLabel.clinic", value: null };
  if (!order) return clinicNone;

  // 1. Patient destination wins — a drop-ship / referral / patient-placed order
  //    is a Patient card even if a staffer entered it.
  const isPatientDestination = isPeptideShipToPatient(order) || isPeptideReferralLinkOrder(order) || !!order.placedByPatient;
  if (isPatientDestination) {
    return { key: "patient", labelKey: "peptides.kanban.metaLabel.patient", value: cardActorPatientName(order) };
  }

  // 2. In-store walk-in counter sale → the staff member who rang it up, shown
  //    as just the person (the station/role prefix is stripped).
  if (order.assignType === "instore") {
    const placedBy =
      (typeof order.placedByName === "string" && order.placedByName.trim()) || cardActorHistoryPlacer(order) || null;
    return { key: "instore", labelKey: "peptides.kanban.metaLabel.instore", value: instorePlacerPerson(placedBy) };
  }

  // 3. Everything else is a clinic-stock order → the clinic / org name.
  const clinic = order.assignedProviderName || order.organizationName || order.placingOrgName || null;
  return { key: "clinic", labelKey: "peptides.kanban.metaLabel.clinic", value: clinic };
};

// Statuses where the order has not yet reached fulfillment and review is still
// meaningful. Beyond this set the order has either shipped, been delivered, or
// been cancelled — surfacing them in the Draft lane would be confusing.
const REVIEW_GATED_STATUSES = ["draft", "payment_pending", "confirmed"];

// True when the manufacturer must review this order before it can leave the
// Draft lane. Manufacturer-only concept — providers and patients always see
// false.
//
// Two runtime quirks this classifier has to dance around, both verified on
// the manufacturer-list endpoint's projection:
//   1. `source` is stripped, so we can't use isPeptideProviderPlaced. Instead
//      we use `paymentMethod` presence as the inverse: PeptideCheckout.js:449
//      only writes paymentMethod for the non-manufacturer branch, so its
//      presence reliably means "this was placed by a provider or patient,
//      not the manufacturer themselves."
//   2. `manufacturerReviewed` is stripped too, even though updateDraftOrder
//      DOES persist it on the order document (verified via the SSE
//      notification echo). To work around this without a backend change we
//      gate on `totalCents > 0` as a proxy: provider-placed orders ship with
//      totalCents = 0 from the manufacturer-list projection, and a non-zero
//      value indicates the manufacturer has touched the pricing (which only
//      happens via this very review flow's updateDraftOrder call). The
//      `manufacturerReviewed` check is kept as belt-and-suspenders so that
//      the day the backend adds the field to the list projection, the gate
//      automatically uses it without a code change.
export const isPeptideAwaitingManufacturerReview = (order, { isManufacturerView } = {}) =>
  !!isManufacturerView &&
  !!order?.paymentMethod &&
  !(order?.totalCents > 0) &&
  !order?.manufacturerReviewed &&
  REVIEW_GATED_STATUSES.includes(order?.status);

// Renamed from isPeptidePatientPlacedAwaitingProvider — under the pay-at-
// submit model, "patient-placed" orders are paid (auth held). The new name
// reflects that payment has occurred and we're waiting on provider review.
export const isPeptidePatientPaidAwaitingProvider = (order, { isProviderView } = {}) =>
  !!isProviderView &&
  !!order?.placedByPatient &&
  !order?.providerApproved &&
  !order?.providerDeniedReason &&
  order?.status === "payment_authorized";

export const isPaymentFailed = (order) => order?.status === "payment_failed";

// Patient can self-cancel only while the auth is held and the provider
// hasn't acted. Once captured (confirmed) or shipped, no patient cancel.
export const isPatientCancellable = (order) => order?.status === "payment_authorized";

// Denied = explicit "denied" OR the backend's manufacturer-deny shape, which
// lands as status "cancelled" WITH a providerDeniedReason. Shared so the card
// header and body can never disagree about the same order.
export const isOrderDenied = (order) =>
  order?.status === "denied" || (order?.status === "cancelled" && !!order?.providerDeniedReason);

// Safe fallback chain for the "Referring Provider" / "Ordered By" card on the
// manufacturer fulfillment view (and its printable PDF mirror). Critical: on a
// ship-to-patient order `order.shipping` is the patient, so this chain MUST
// NOT reach into shipping when `referringProviderName` is missing — that's
// what put the patient's name on the provider card before. `providerOrg` is
// the live org-by-id lookup available in the JSX path; the PDF passes nothing
// for it, which is fine — the chain falls through to the stamped fields.
export const getReferringProviderName = (order, providerOrg = null) =>
  order?.referringProviderName || providerOrg?.name || order?.organizationName || order?.organizationId || "Unknown";

// Effective per-unit price for an order line: a saved custom override wins, then
// the as-placed `priceCents` snapshot (the price locked in at creation time), and
// only as a last resort the live tier resolve. Single source of truth for the
// fallback rule so the draft-review drawer and the drag-flow Approve modal can't
// drift. `resolvePrice` is the usePeptidePricing resolver; it may return null.
export const peptideEffectivePrice = (item, resolvePrice) =>
  item?.customPriceCents ?? item?.priceCents ?? resolvePrice?.(item?.variantId);

// Resolve a set of order lines to {resolvedItems, totalCents} using
// peptideEffectivePrice. Shared by every surface that finalizes an order so the
// total shown always matches the total persisted.
export const resolveDraftPricing = (items, resolvePrice) => {
  // Coerce an unresolved price to 0 — peptideEffectivePrice returns null/undefined when
  // an item has no custom/as-placed price and resolvePrice falls through. Without this,
  // the per-line `priceCents` is persisted as null while the total (which already
  // guards with `|| 0`) silently omits that line, so the two disagree.
  const resolvedItems = (items || []).map((it) => ({ ...it, priceCents: peptideEffectivePrice(it, resolvePrice) ?? 0 }));
  const totalCents = resolvedItems.reduce((sum, it) => sum + (it.priceCents || 0) * (it.quantity || 0), 0);
  return { resolvedItems, totalCents };
};

// Decides what payload to send to updateDraftOrder and whether to follow up
// with finalizeDraftOrder, based on the order's current status. Exact drafts
// (status "draft") write items/total + un-archive + finalize; non-draft
// provider-placed orders being reviewed (status payment_pending or confirmed) are
// already past draft, so their placed prices, items, and shipping are immutable.
// Those orders receive only the review stamp and never finalize, since finalizing
// could also backward-transition a confirmed order to payment_pending.
export const getPeptideDraftApprovePlan = (order, items, totalCents, shippingUpdate) => {
  const isMfrOwnDraft = order?.status === "draft";
  const shippingPart = shippingUpdate ? { shipping: shippingUpdate } : {};
  return isMfrOwnDraft
    ? { payload: { items, totalCents, isArchived: false, manufacturerReviewed: true, ...shippingPart }, shouldFinalize: true }
    : { payload: { manufacturerReviewed: true }, shouldFinalize: false };
};

// Apply a single delta to every tier's margin, clamping each independently.
// Tiers whose current margin is null (cost unknown / never set) are left alone.
// Used by the "Adjust all tiers together" lock-spread mode in the variants editor.
export const applyTierMarginsDelta = (currentMargins, deltaPct) => {
  const out = {};
  Object.keys(currentMargins).forEach((tier) => {
    const cur = currentMargins[tier];
    out[tier] = cur == null ? null : clampMargin(cur + deltaPct);
  });
  return out;
};

// Per-tier default margin ladder used when a manufacturer types cost on a brand-new
// variant, and again when they accept the "recompute tier prices" prompt after
// changing cost on an existing variant. Reflects industry-typical wholesale
// markup ladder: A (top partners) gets the steepest discount = smallest margin
// to manufacturer; W (retail/public) gets the full MSRP markup.
export const DEFAULT_TIER_MARGINS = { A: 40, B: 45, C: 50, D: 55, W: 70 };

// Per-unit surcharge added when a peptide line is shipped in "active"
// (pre-mixed liquid) form instead of lyophilized powder. Lyophilized ships at
// the catalog tier price; active adds preparation/cold-chain cost on top.
// Mirror this on the server when it recomputes totalCents (see backend guide
// 2026-05-20-peptides-recommended-and-active-surcharge.md) — without that, the
// FE submitted total will not match the BE recalculated total.
export const ACTIVE_FORM_SURCHARGE_CENTS = 500;

// Apply the active-form surcharge to a base unit price. `formType` may be
// undefined while the user is still picking; only "active" triggers the
// surcharge. Returns the base unchanged when it's null so price-missing
// states (e.g. no tier configured for a variant) propagate through the cart
// math instead of accidentally inflating to $5.
export const applyFormSurcharge = (baseCents, formType, enabled = true) => {
  if (baseCents == null) return baseCents;
  return formType === "active" && enabled ? baseCents + ACTIVE_FORM_SURCHARGE_CENTS : baseCents;
};

// Per-provider active-form surcharge state, read from the exceptions-only map
// `peptideProviderSurchargeDisabled` ({ providerOrgId: true }). Absent / non-true
// entry = enabled (the default). Mirrors the BE reDerive gate.
export const isProviderSurchargeEnabled = (surchargeDisabledMap, providerOrgId) =>
  !(surchargeDisabledMap && surchargeDisabledMap[providerOrgId] === true);

// True when every cart line is ready to check out from a form-type standpoint:
// each peptide must have "active" or "lyophilized" picked, while supplies
// (bacteriostatic water, syringes — `category === "supplies"`) are exempt
// because they ship as-is with no physical-form choice. Single source of truth
// shared by usePeptideCart's `allFormsSelected` memo AND the checkout's
// fail-closed submit guard, so the two paths can't drift (the previous bug:
// supplies passed the cart gate but tripped the inline checkout predicate).
export const hasAllFormsSelected = (items) =>
  Array.isArray(items) &&
  items.length > 0 &&
  items.every((item) => item.product?.category === "supplies" || item.formType === "active" || item.formType === "lyophilized");

// Identify the Reconstitution Kit supply product (5ml bacteriostatic water +
// insulin syringes + loading needle + loading syringe, sold as one SKU).
// Matched by id and name so a catalog rename that drops the canonical id slug
// still resolves. MUST be checked before isBacteriostaticWaterProduct — a kit
// name that advertises its contents ("...5ml Bacteriostatic Water...") also
// matches the water name pattern, and whichever matcher runs first wins.
const RECON_KIT_ID_PATTERN = /^reconstitution[-_]?kit/i;
const RECON_KIT_NAME_PATTERN = /reconstitution\s*kit/i;
export const isReconstitutionKitProduct = (product) => {
  if (!product) return false;
  if (RECON_KIT_ID_PATTERN.test(product.id || "")) return true;
  return RECON_KIT_NAME_PATTERN.test(product.name || "");
};

// Identify the bacteriostatic-water supply product. Matched by both id and
// name so a catalog rename that drops the canonical id slug still resolves
// (the prompt under lyophilized items relies on this). Used by the cart's
// "Lyophilized requires bac water" prompt to find which catalog product to
// offer for one-click add.
const BAC_WATER_ID_PATTERN = /^bacteriostatic[-_]?water/i;
const BAC_WATER_NAME_PATTERN = /bacteriostatic\s*water/i;
export const isBacteriostaticWaterProduct = (product) => {
  if (!product) return false;
  // A kit is not bac water, even though its name may contain the phrase.
  if (isReconstitutionKitProduct(product)) return false;
  if (BAC_WATER_ID_PATTERN.test(product.id || "")) return true;
  return BAC_WATER_NAME_PATTERN.test(product.name || "");
};

// Checkout recommendations use a stable catalog proxy for "lowest dose": the
// lowest finite list price. A single pass keeps the original catalog order for
// ties and for variants without a price, without mutating product.variants.
const hasStableRecommendationId = (id) => id != null && (typeof id !== "string" || id.trim().length > 0);
const stableRecommendationIdKey = (id) => String(id);

export const getDefaultPeptideVariant = (product) => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  let selected = null;
  let lowestPrice = Infinity;

  variants.forEach((variant) => {
    if (!variant || typeof variant !== "object" || !hasStableRecommendationId(variant.id)) return;
    if (!selected) selected = variant;

    if (Number.isFinite(variant.priceCents) && variant.priceCents < lowestPrice) {
      selected = variant;
      lowestPrice = variant.priceCents;
    }
  });

  return selected;
};

// Preserve catalog order while finding products that share at least one exact
// tag with an item in the cart. Exclusions are product-wide: if any variant is
// already represented (or explicitly excluded), none of that product is shown.
export const getTagMatchedRecommendations = (products, cartItems, options = {}) => {
  const catalog = Array.isArray(products) ? products : [];
  const cart = Array.isArray(cartItems) ? cartItems : [];
  if (catalog.length === 0 || cart.length === 0) return [];

  const fallbackToCatalog = options?.fallbackToCatalog === true;
  const membershipKey = fallbackToCatalog ? stableRecommendationIdKey : (id) => id;
  const catalogByProductId = new Map(
    catalog
      .filter((product) => hasStableRecommendationId(product?.id))
      .map((product) => [stableRecommendationIdKey(product.id), product])
  );

  const cartTags = new Set();
  cart.forEach((item) => {
    const catalogProduct =
      fallbackToCatalog && hasStableRecommendationId(item?.product?.id)
        ? catalogByProductId.get(stableRecommendationIdKey(item.product.id))
        : null;
    const sourceTags = Array.isArray(catalogProduct?.tags) ? catalogProduct.tags : item?.product?.tags;
    if (!Array.isArray(sourceTags)) return;
    sourceTags.forEach((tag) => {
      if (typeof tag === "string" && tag.trim().length > 0) cartTags.add(tag);
    });
  });
  if (cartTags.size === 0 && !fallbackToCatalog) return [];

  const cartProductIds = new Set(
    cart
      .map((item) => item?.product?.id)
      .filter(hasStableRecommendationId)
      .map(membershipKey)
  );
  const cartVariantIds = new Set(
    cart
      .map((item) => item?.variant?.id)
      .filter(hasStableRecommendationId)
      .map(membershipKey)
  );
  const configuredExclusions = options?.excludeVariantIds;
  const excludedVariantIds = fallbackToCatalog
    ? new Set(
        (configuredExclusions instanceof Set
          ? Array.from(configuredExclusions)
          : Array.isArray(configuredExclusions)
            ? configuredExclusions
            : []
        )
          .filter(hasStableRecommendationId)
          .map(stableRecommendationIdKey)
      )
    : configuredExclusions instanceof Set
      ? configuredExclusions
      : Array.isArray(configuredExclusions)
        ? new Set(configuredExclusions)
        : new Set();
  const requestedLimit = options?.limit;
  const limit = requestedLimit === undefined ? 6 : Number.isInteger(requestedLimit) && requestedLimit >= 0 ? requestedLimit : 6;
  // Curated recommendation surfaces (checkout strips) exclude supplies. The
  // Quick Add drawer's "commonly added" nudge on the CART opts in via this flag
  // so required companions like bacteriostatic water still surface there.
  const includeSupplies = options?.includeSupplies === true;
  const seenProductKeys = new Set();
  const hasMatchingCartTag = (product) =>
    Array.isArray(product.tags) &&
    product.tags.some((tag) => typeof tag === "string" && tag.trim().length > 0 && cartTags.has(tag));

  const eligibleProducts = catalog
    .filter(
      (product) => hasStableRecommendationId(product?.id) && typeof product?.name === "string" && product.name.trim().length > 0
    )
    .filter((product) => !cartProductIds.has(membershipKey(product.id)))
    .filter(
      (product) =>
        includeSupplies ||
        (product.category !== "supplies" && !isBacteriostaticWaterProduct(product) && !isReconstitutionKitProduct(product))
    )
    .filter((product) => {
      const defaultVariant = getDefaultPeptideVariant(product);
      if (!defaultVariant || !hasStableRecommendationId(defaultVariant.id)) return false;

      const variants = Array.isArray(product.variants) ? product.variants : [];
      if (
        variants.some(
          (variant) =>
            variant && (cartVariantIds.has(membershipKey(variant.id)) || excludedVariantIds.has(membershipKey(variant.id)))
        )
      ) {
        return false;
      }

      // Fail closed on duplicate API rows so one product cannot occupy both a
      // protocol bundle and its non-overlapping "Other" slice (or be added
      // twice by the bundle action). Preserve the first catalog occurrence.
      // Strict callers historically filtered tags before de-duplicating, so a
      // nonmatching row must not suppress a later matching row with the same ID.
      if (!fallbackToCatalog && !hasMatchingCartTag(product)) return true;
      const productKey = `product:${fallbackToCatalog ? stableRecommendationIdKey(product.id) : product.id}`;
      if (seenProductKeys.has(productKey)) return false;
      seenProductKeys.add(productKey);
      return true;
    });

  const tagMatches = eligibleProducts.filter(hasMatchingCartTag);

  if (!fallbackToCatalog) return tagMatches.slice(0, limit);

  const selected = [];
  const selectedProductIds = new Set();
  const append = (product) => {
    const key = stableRecommendationIdKey(product.id);
    if (selected.length >= limit || selectedProductIds.has(key)) return;
    selectedProductIds.add(key);
    selected.push(product);
  };

  tagMatches.forEach(append);
  eligibleProducts.filter((product) => product.isRecommended === true).forEach(append);
  eligibleProducts.forEach(append);
  return selected;
};

// ── Provider-checkout helpers ──
export const shouldShowCheckoutRecommendations = ({ isManufacturer = false, isSalesRep = false } = {}) =>
  !isManufacturer && !isSalesRep;

// Pure predicate: provider-placed orders that ship to a patient must have a
// real selectedPatientId. Manufacturer flow is unaffected — manufacturers can
// still freehand patient info for master/draft orders. Sales reps using the
// provider checkout for personal orders are also exempt: there is no patient
// — the rep is the buyer and the shipping form captures their own address.
// Centralizing so PeptideCheckout's submit guard and any future call sites
// share one rule.
export const requiresPatientSelection = ({ isManufacturer, isSalesRep, shippingMode, selectedPatientId }) =>
  !isManufacturer && !isSalesRep && shippingMode === "patient" && !selectedPatientId;

// Compose the placing provider's display name for stamping onto a peptide
// order at create time. Returns "First Last, Credentials" or "First Last"
// when credentials is missing/"Other"; returns undefined when no name is
// available so the consumer can omit the field entirely.
export const composePlacingProviderName = ({ firstName, lastName, credentials } = {}) => {
  const name = `${firstName || ""} ${lastName || ""}`.trim();
  if (!name) return undefined;
  if (!credentials || credentials === "Other") return name;
  return `${name}, ${credentials}`;
};

// Filter the kanban order list to a single patient when the page is launched
// with `?patientId=…` (from the clinical profile "View all N orders" link).
// Pulled out as a pure helper so we can unit-test the predicate without
// mounting the whole page (which depends on auth, dnd, react-router, i18n).
// Field name matches `filterAndAggregateForPatient` and the backend write
// path: `assignedPatientId`. When no patientId is supplied we return the
// list unchanged so callers can render the unfiltered kanban.
export const filterOrdersByPatientId = (orders, patientId) => {
  if (!patientId) return orders || [];
  return (orders || []).filter((o) => o?.assignedPatientId === patientId);
};

// Provider "My Previous Orders" history: the flat, date-sorted list of orders
// THIS clinic placed. "Mine" is org-scoped — the order-list projection does not
// expose an individual placing-provider id (see getPeptideOrders' TODO(BE)), so
// getOrderOrigin (which resolves provider-placed orders via the org-slug `source`
// tag stamped in getPeptideOrders) is the finest-grained "mine" signal available,
// and it is exactly what the kanban "My" chip uses. Archived orders are excluded;
// newest first by placement date (derivePatientOrderPlacedAt normalizes the
// several date fields, incl. epoch-ms createdOn).
export const selectMyPlacedOrders = (orders) =>
  (Array.isArray(orders) ? orders : [])
    .filter((o) => !o?.isArchived && getOrderOrigin(o) === "provider")
    .sort((a, b) => {
      const ax = new Date(derivePatientOrderPlacedAt(a) || 0).getTime();
      const bx = new Date(derivePatientOrderPlacedAt(b) || 0).getTime();
      return bx - ax;
    });

// Client-side search for the provider Order History surface. The list is
// already loaded in full, so keeping this pure avoids a second request path
// and makes malformed legacy order projections harmless.
export const filterPeptideOrdersBySearch = (orders, query) => {
  if (!Array.isArray(orders)) return [];
  const needle = String(query ?? "")
    .trim()
    .toLocaleLowerCase();
  if (!needle) return orders;

  return orders.filter((order) => {
    const id = String(order?.id ?? "");
    const patientName = `${String(order?.shipping?.firstName ?? "")} ${String(order?.shipping?.lastName ?? "")}`;
    const productNames = (Array.isArray(order?.items) ? order.items : [])
      .map((item) => item?.productName ?? item?.name ?? item?.product?.name ?? "")
      .join(" ");
    const haystack = `${id} #${id} ${patientName} ${productNames}`.toLocaleLowerCase();
    return haystack.includes(needle);
  });
};

// Shared status-tab bar config for the patient "My Orders" and provider
// "Order History" list pages — same four buckets, same i18n keys, same
// default-bucket rule. Both pages render `PEPTIDE_ORDER_TABS` identically
// and filter via `getPeptideOrderTabBucket`.
export const PEPTIDE_ORDER_TABS = [
  { id: "all", labelKey: "peptides.orders.tabAll", labelDefault: "All" },
  { id: "active", labelKey: "peptides.orders.tabActive", labelDefault: "Active" },
  { id: "delivered", labelKey: "peptides.orders.tabDelivered", labelDefault: "Delivered" },
  { id: "canceled", labelKey: "peptides.orders.tabCanceled", labelDefault: "Canceled" },
];

export const getPeptideOrderTabBucket = (status) => PEPTIDE_STATUSES[status]?.patientBucket ?? "all";

// Used to build the per-tab empty-state i18n key (peptides.orders.empty{Bucket}).
export const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Decide which reconstitution companion product (if any) to offer for the
// current cart. Pure — takes the cart lines and the catalog, returns the
// single prompt to show.
//
// A lyophilized vial needs bacteriostatic water before injection. For a
// one-vial order we can cover the whole job with one Reconstitution Kit SKU
// (5ml water + syringes + loading needle). Past one vial the kit's 5ml no
// longer stretches, so we fall back to offering water.
//
// Coverage assumption: one kit covers one vial (see the design doc's named
// assumption). If 5ml turns out to reconstitute more vials, the
// `lyoVials - kitQty` subtraction below is the only place to change.
//
// Resolution order matters:
//   1. water already in cart -> silent, whatever the vial count (preserves
//      the pre-kit behavior: someone who bought water is never nudged)
//   2. vials fully covered by kits in cart -> silent
//   3. exactly one uncovered vial and no kit yet -> kit
//   4. otherwise -> water
// Any branch degrades to `null` when the catalog lacks the product it would
// have offered, so a provider/patient catalog without these supplies simply
// shows no prompt rather than a dead button.
export const resolveReconPrompt = ({ items, products } = {}) => {
  const none = { kind: null, product: null };
  const cart = Array.isArray(items) ? items : [];
  const catalog = Array.isArray(products) ? products : [];

  const kitProduct = catalog.find(isReconstitutionKitProduct) || null;
  const waterProduct = catalog.find(isBacteriostaticWaterProduct) || null;

  const variantIdsOf = (product) => new Set((product?.variants || []).map((v) => v.id));
  const qtyInCart = (product) => {
    if (!product) return 0;
    const ids = variantIdsOf(product);
    return cart.filter((item) => ids.has(item.variant?.id)).reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  if (qtyInCart(waterProduct) > 0) return none;

  const lyoVials = cart.filter((item) => item.formType === "lyophilized").reduce((sum, item) => sum + (item.quantity || 0), 0);
  const kitQty = qtyInCart(kitProduct);

  if (lyoVials - kitQty <= 0) return none;

  if (lyoVials === 1 && kitQty === 0 && kitProduct) {
    return { kind: "kit", product: kitProduct };
  }
  return waterProduct ? { kind: "water", product: waterProduct } : none;
};
